import * as React from 'react';
import type { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import type { BeneficialOwnershipSpecificMetadata, CustomField, CustomSection } from '@gcv/shared';
import { Box } from '@mui/material';

import { ReactComponent as DuplicateIcon } from 'assets/images/ic-duplicate.svg';
import { ReactComponent as EditIcon } from 'assets/images/ic-edit.svg';
import { ReactComponent as RulesIcon } from 'assets/images/ic-rules.svg';
import { ReactComponent as DeleteIcon } from 'assets/images/ic-trash.svg';
import { ReactComponent as MoveControl } from 'assets/images/move-control.svg';
import { Card, Container, ContainerItem, Drag, Drop, Hyperlink, Text, Title, Tooltip } from 'ui';
import palette from 'ui/theme/palette';
import { FieldDisplay } from '.';
import { interpolateValuesInText } from './interpolateValuesInText';
import { SectionWeightPanel } from './section-weight-panel';
import type { SectionWeightSummary } from './section-weight-summary.util';
import { getSectionWeightSummary } from './section-weight-summary.util';
import { WeightInput } from './weight-input';

interface Props {
  dragHandleProps: DraggableProvidedDragHandleProps | undefined | null;
  openFieldModal: (isOpen: boolean, fieldId?: string, sectionId?: string) => void;
  openFieldDeleteModal: (isOpen: boolean, fieldId: string) => void;
  openSectionModal: (isOpen: boolean, sectionId?: string) => void;
  openSectionDeleteModal: (isOpen: boolean, sectionId: string) => void;
  openSmartRuleModal: (field: CustomField) => void;
  openSmartRuleModalForSection: (section: CustomSection) => void;
  section: CustomSection;
  viewOnly?: boolean;
  viewOnlyTooltip?: string;
  duplicateSection?: (type: 'field' | 'section', data: CustomField | CustomSection) => void;
  duplicateItemIds: string[];
  interpolationValues?: BeneficialOwnershipSpecificMetadata;
  scored?: boolean;
  onSectionWeightChange?: (section: CustomSection, weight: number | undefined) => void;
  onFieldWeightChange?: (field: CustomField, weight: number | undefined) => void;
}

const MaybeWeightPanel: React.FC<{
  show: boolean;
  summary: SectionWeightSummary;
  children: React.ReactNode;
}> = ({ show, summary, children }) =>
  show ? <SectionWeightPanel summary={summary}>{children}</SectionWeightPanel> : <>{children}</>;

export const SectionDisplay: React.FC<Props> = (props) => {
  const animation = props.duplicateItemIds.includes(props.section.id) ? '3s linear SMART_RULE_ANIM' : null;
  const sectionWeight = (props.section as { weight?: number }).weight;

  // Same scored-question rule as the publish gate, so the panel and the gate always agree.
  const weightSummary = getSectionWeightSummary(props.section);
  const showWeightPanel = !!props.scored && weightSummary.scoredFieldIds.length > 0;

  return (
    <Card
      header={{
        align: 'center',
        title: (
          <Container padding={0} nowrap>
            {!props.viewOnly && (
              <ContainerItem padding={0}>
                <div {...props.dragHandleProps}>
                  <MoveControl
                    style={{ cursor: 'grab', marginRight: '1rem' }}
                    data-cy="section-grab-control"
                  />
                </div>
              </ContainerItem>
            )}
            <ContainerItem padding={0}>
              {props.section.label.length > 50 ? (
                <Title
                  content={
                    interpolateValuesInText(props.section.label, props.interpolationValues).substring(0, 50) +
                    '...'
                  }
                  Tooltip={interpolateValuesInText(props.section.label, props.interpolationValues)}
                  justify="flex-start"
                  type={'h2'}
                  dataCy="section-title"
                />
              ) : (
                <Title
                  content={interpolateValuesInText(props.section.label, props.interpolationValues)}
                  justify="flex-start"
                  type={'h2'}
                  dataCy="section-title"
                />
              )}
              <Text
                sx={{ whiteSpace: 'pre-line' }}
                content={interpolateValuesInText(props.section.description, props.interpolationValues)}
                type="body2"
                dataCy="section-description"
              />
              {showWeightPanel && (
                <Text
                  content={`${weightSummary.scoredFieldIds.length} scored question${
                    weightSummary.scoredFieldIds.length === 1 ? '' : 's'
                  }`}
                  type="body3"
                  sx={{ color: palette.text.secondary }}
                  dataCy="section-scored-question-count"
                />
              )}
            </ContainerItem>
          </Container>
        ),
        actions: !props.viewOnly ? (
          <Container padding={0} nowrap align="center">
            {props.scored && (
              <ContainerItem padding="0 0.5rem">
                <WeightInput
                  variant="inline"
                  value={sectionWeight}
                  onCommit={(weight) => props.onSectionWeightChange?.(props.section, weight)}
                  tooltip="Percentage of the total score this section contributes. Enter a whole number from 0 to 100; section weights should total 100%."
                  ariaLabel={`Weight for ${props.section.label}`}
                  dataCy="section-weight-input"
                />
              </ContainerItem>
            )}
            {!props.scored && (
              <ContainerItem padding="0 0.5rem">
                <Tooltip placement="bottom" title="Smart Rules">
                  <Box
                    sx={{
                      '&:hover path': { fill: props.section.smart_rule ? '#03A65B' : '#3A3C45' },
                      path: { fill: props.section.smart_rule ? '#03A65B' : '#C6CCDA' }
                    }}
                  >
                    <RulesIcon
                      style={{ cursor: 'pointer', height: '24px' }}
                      onClick={() => {
                        props.openSmartRuleModalForSection(props.section);
                      }}
                      data-cy="edit-field-icon"
                    />
                  </Box>
                </Tooltip>
              </ContainerItem>
            )}

            <ContainerItem padding="0 0.5rem">
              <Tooltip placement="bottom" title="Duplicate">
                <Box
                  sx={{
                    '&:hover path': { fill: '#3A3C45' }
                  }}
                >
                  <DuplicateIcon
                    style={{ cursor: 'pointer', height: '24px' }}
                    onClick={() => {
                      if (props.duplicateSection) {
                        props.duplicateSection('section', props.section);
                      }
                    }}
                    data-cy="duplicate-section-icon"
                  />
                </Box>
              </Tooltip>
            </ContainerItem>

            <ContainerItem padding="0 0.5rem">
              <Tooltip placement="bottom" title="Edit">
                <Box
                  sx={{
                    '&:hover path': { fill: '#3A3C45' }
                  }}
                >
                  <EditIcon
                    style={{ cursor: 'pointer', height: '24px' }}
                    onClick={() => props.openSectionModal(true, props.section.id)}
                    data-cy="edit-section-icon"
                  />
                </Box>
              </Tooltip>
            </ContainerItem>

            <ContainerItem padding="0">
              <Box
                sx={{
                  '&:hover path': { fill: '#3A3C45' }
                }}
              >
                <Tooltip placement="bottom" title="Delete">
                  <DeleteIcon
                    style={{ cursor: 'pointer', height: '24px' }}
                    onClick={() => props.openSectionDeleteModal(true, props.section.id)}
                    data-cy="delete-section-icon"
                  />
                </Tooltip>
              </Box>
            </ContainerItem>
          </Container>
        ) : null,
        nowrap: true
      }}
      dataCy={`section-display-${props.section.id}`}
      contentOverflow="visible"
      animation={animation}
    >
      {props.viewOnly ? (
        <>
          <Container column padding={0} width="100%" dataCy="section-fields">
            {props.section.fields?.map((field, index) => (
              <ContainerItem padding="6px 0" width="100%">
                <FieldDisplay
                  field={field}
                  openFieldModal={props.openFieldModal}
                  openFieldDeleteModal={props.openFieldDeleteModal}
                  dragHandleProps={undefined}
                  openSmartRuleModal={props.openSmartRuleModal}
                  viewOnly={props.viewOnly}
                  viewOnlyTooltip={props.viewOnlyTooltip}
                  duplicateField={props.duplicateSection}
                  duplicateItemIds={props.duplicateItemIds}
                  scored={props.scored}
                  onFieldWeightChange={props.onFieldWeightChange}
                />
              </ContainerItem>
            ))}
          </Container>
        </>
      ) : (
        <Container column padding={0} width="100%" dataCy="section-fields">
          <Drop
            id={props.section.id}
            type="FIELD"
            renderContainer={(content) => (
              <MaybeWeightPanel show={showWeightPanel} summary={weightSummary}>
                {content}
              </MaybeWeightPanel>
            )}
            footer={
              <ContainerItem justify="center" width="100%">
                <Hyperlink
                  label="+ Drag or Add New Field"
                  color="green"
                  onClick={() => props.openFieldModal(true, undefined, props.section.id)}
                  dataCy="drag-or-add-new-field-button"
                />
              </ContainerItem>
            }
          >
            {props.section.fields?.map((field, index) => (
              <Drag key={field.id} id={field.id} index={index}>
                {(dragHandle) => (
                  <ContainerItem padding={0} width="100%">
                    <FieldDisplay
                      field={field}
                      openFieldModal={props.openFieldModal}
                      openFieldDeleteModal={props.openFieldDeleteModal}
                      dragHandleProps={dragHandle}
                      openSmartRuleModal={props.openSmartRuleModal}
                      viewOnly={props.viewOnly}
                      duplicateField={props.duplicateSection}
                      duplicateItemIds={props.duplicateItemIds}
                      scored={props.scored}
                      onFieldWeightChange={props.onFieldWeightChange}
                    />
                  </ContainerItem>
                )}
              </Drag>
            ))}
          </Drop>
        </Container>
      )}
    </Card>
  );
};
